"use client"

import type React from "react"
import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { CheckCircle, MessageCircle, Calendar, Video, Shield } from "lucide-react"

interface FormData {
	name: string
	phone: string
	platform: string
	service: string
	timezone: string
	message: string
}

export default function Booking() {
	const [formData, setFormData] = useState<FormData>({
		name: "",
		phone: "",
		platform: "",
		service: "",
		timezone: "",
		message: "",
	})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [showSuccess, setShowSuccess] = useState(false)

	const handleInputChange = (field: keyof FormData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }))
	}

	const formatPhoneNumber = (value: string) => {
		const cleaned = value.replace(/\D/g, "");
		let result = "+7";
		let number = cleaned;

		if (number.startsWith("8")) {
			number = "7" + number.slice(1);
		}
		if (number.startsWith("7")) {
			number = number.slice(1); // remove leading 7
		}

		if (number.length > 0) {
			result += " (" + number.substring(0, 3);
		}
		if (number.length >= 4) {
			result += ") " + number.substring(3, 6);
		}
		if (number.length >= 7) {
			result += "-" + number.substring(6, 8);
		}
		if (number.length >= 9) {
			result += "-" + number.substring(8, 10);
		}

		return result;
	}

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatPhoneNumber(e.target.value)
		handleInputChange("phone", formatted)
	}

	const validateForm = () => {
		if (!formData.name || !formData.phone || !formData.platform || !formData.service) {
			alert("Пожалуйста, заполните все обязательные поля")
			return false
		}

		const phoneRegex = /^[+]?[0-9\s\-()]{10,}$/
		if (!phoneRegex.test(formData.phone)) {
			alert("Пожалуйста, введите корректный номер телефона")
			return false
		}

		return true
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		if (!validateForm()) return

		setIsSubmitting(true)

		try {
			const res = await fetch("/api/send-telegram", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(formData),
			})
			if (!res.ok) {
				throw new Error("Ошибка отправки заявки. Попробуйте позже или напишите в мессенджер.")
			}
			setShowSuccess(true)
			setFormData({
				name: "",
				phone: "",
				platform: "",
				service: "",
				timezone: "",
				message: "",
			})
		} catch (err) {
			alert(err instanceof Error ? err.message : "Ошибка отправки. Попробуйте позже.")
		} finally {
			setIsSubmitting(false)
		}
	}

	
	const steps = [
		{ icon: MessageCircle, text: "Заполните форму или напишите в WhatsApp/Telegram" },
		{ icon: Calendar, text: "Я свяжусь с вами в течение 24 часов" },
		{ icon: Video, text: "Согласуем время и платформу для сессии" },
		{ icon: Shield, text: "Проводим конфиденциальную онлайн-сессию" },
	]

	return (
		<section id="booking" className="py-6 sm:py-12 lg:py-12 bg-white">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="text-center mb-12 sm:mb-16">
					<h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">Записаться или задать вопрос</h2>
					<p className="text-base sm:text-xl text-slate-600">Расскажите о вашей проблеме и выясните, могу ли я вам помочь</p>
				</div>
				
				{/* Quick Contact Options */}
				<div className="bg-indigo-50 rounded-2xl p-6 sm:p-8 mb-12 sm:mb-16 max-w-4xl mx-auto">
					<h3 className="text-xl sm:text-2xl font-semibold text-slate-900 text-center mb-4 sm:mb-6">Быстрая связь через мессенджеры</h3>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<a
							href={`https://wa.me/79226268052?text=${encodeURIComponent('Здравствуйте! Хочу записаться на онлайн-консультацию.')}`}
							target="_blank"
							rel="noopener noreferrer"
							className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2 text-lg"
						>
							<MessageCircle className="h-5 w-5" />
							WhatsApp
						</a>
						<a
							href="https://t.me/OxanaZayakina"
							target="_blank"
							rel="noopener noreferrer"
							className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center justify-center gap-2 text-lg"
						>
							<MessageCircle className="h-5 w-5" />
							 Telegram
						</a>
					</div>
					<p className="text-center text-slate-600 mt-4 text-sm sm:text-base">Или заполните форму ниже, и я свяжусь с вами</p>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 max-w-6xl mx-auto">
					{/* Process Steps */}
					<div className="order-2 lg:order-1">
						<Card className="bg-slate-50 border-slate-200">
							<CardHeader>
								<CardTitle className="text-xl sm:text-2xl text-slate-900">Как проходит запись</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4 sm:space-y-6">
									{steps.map((step, index) => (
										<div key={index} className="flex items-start gap-3 sm:gap-4">
											<div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">{index + 1}</div>
											<p className="text-sm sm:text-base text-slate-700 leading-relaxed pt-1">{step.text}</p>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					</div>

					{/* Booking Form */}
					<Card className="bg-white border-slate-200 shadow-lg order-1 lg:order-2">
						<CardHeader>
							<CardTitle className="text-xl sm:text-2xl text-slate-900">Форма записи</CardTitle>
						</CardHeader>
						<CardContent>
							<form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label htmlFor="name" className="text-sm font-medium text-slate-700">
											Ваше имя *
										</Label>
										<Input
											id="name"
											value={formData.name}
											onChange={(e) => handleInputChange("name", e.target.value)}
											placeholder="Введите ваше имя"
											className="text-sm sm:text-base bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-indigo-500"
											required
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="phone" className="text-sm font-medium text-slate-700">
											Телефон *
										</Label>
										<Input
											id="phone"
											value={formData.phone}
											onChange={handlePhoneChange}
											placeholder="+7 (999) 123-45-67"
											className="text-sm sm:text-base bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-indigo-500"
											required
										/>
									</div>
								</div>

								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label className="text-sm font-medium text-slate-700">Платформа для сессии *</Label>
										<Select value={formData.platform} onValueChange={(value) => handleInputChange("platform", value)}>
											<SelectTrigger className="text-sm sm:text-base bg-white border-slate-300 text-slate-900 focus:border-indigo-500 focus:ring-indigo-500">
												<SelectValue placeholder="Выберите платформу" />
											</SelectTrigger>
											<SelectContent className="bg-white border-slate-300">
												<SelectItem value="google meet" className="text-slate-900 hover:bg-slate-100">
													Google Meet
												</SelectItem>
												<SelectItem value="telegram" className="text-slate-900 hover:bg-slate-100">
													Telegram
												</SelectItem>
												<SelectItem value="whatsapp" className="text-slate-900 hover:bg-slate-100">
													WhatsApp
												</SelectItem>
											</SelectContent>
										</Select>
									</div>

									<div className="space-y-2">
										<Label className="text-sm font-medium text-slate-700">Часовой пояс</Label>
										<Select value={formData.timezone} onValueChange={(value) => handleInputChange("timezone", value)}>
											<SelectTrigger className="text-sm sm:text-base bg-white border-slate-300 text-slate-900 focus:border-indigo-500 focus:ring-indigo-500">
												<SelectValue placeholder="Ваш часовой пояс" />
											</SelectTrigger>
											<SelectContent className="bg-white border-slate-300">
												<SelectItem value="Калининград (UTC+2)" className="text-slate-900 hover:bg-slate-100">
													Калининград (UTC+2)
												</SelectItem>
												<SelectItem value="Москва (UTC+3)" className="text-slate-900 hover:bg-slate-100">
													Москва (UTC+3)
												</SelectItem>
												<SelectItem value="Самара (UTC+4)" className="text-slate-900 hover:bg-slate-100">
													Самара (UTC+4)
												</SelectItem>
												<SelectItem value="Екатеринбург (UTC+5)" className="text-slate-900 hover:bg-slate-100">
													Екатеринбург (UTC+5)
												</SelectItem>
												<SelectItem value="Омск (UTC+6)" className="text-slate-900 hover:bg-slate-100">
													Омск (UTC+6)
												</SelectItem>
												<SelectItem value="Красноярск (UTC+7)" className="text-slate-900 hover:bg-slate-100">
													Красноярск (UTC+7)
												</SelectItem>
												<SelectItem value="Иркутск (UTC+8)" className="text-slate-900 hover:bg-slate-100">
													Иркутск (UTC+8)
												</SelectItem>
												<SelectItem value="Якутск (UTC+9)" className="text-slate-900 hover:bg-slate-100">
													Якутск (UTC+9)
												</SelectItem>
												<SelectItem value="Владивосток (UTC+10)" className="text-slate-900 hover:bg-slate-100">
													Владивосток (UTC+10)
												</SelectItem>
												<SelectItem value="Магадан (UTC+11)" className="text-slate-900 hover:bg-slate-100">
													Магадан (UTC+11)
												</SelectItem>
												<SelectItem value="Камчатка (UTC+12)" className="text-slate-900 hover:bg-slate-100">
													Камчатка (UTC+12)
												</SelectItem>
											</SelectContent>
										</Select>
									</div>
								</div>

								<div className="space-y-2">
									<Label className="text-sm font-medium text-slate-700">Тип услуги *</Label>
									<Select value={formData.service} onValueChange={(value) => handleInputChange("service", value)}>
										<SelectTrigger className="text-sm sm:text-base bg-white border-slate-300 text-slate-900 focus:border-indigo-500 focus:ring-indigo-500">
											<SelectValue placeholder="Выберите услугу" />
										</SelectTrigger>
										<SelectContent className="bg-white border-slate-300">
											<SelectItem value="therapy" className="text-slate-900 hover:bg-slate-100">
												Психотерапия
											</SelectItem>
											<SelectItem value="psychosomatics" className="text-slate-900 hover:bg-slate-100">
												Психосоматика
											</SelectItem>
											<SelectItem value="hypnotherapy" className="text-slate-900 hover:bg-slate-100">
												Гипнотерапия
											</SelectItem>
											<SelectItem value="coaching" className="text-slate-900 hover:bg-slate-100">
												Коучинг
											</SelectItem>
											
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<Label htmlFor="message" className="text-sm font-medium text-slate-700">
										Кратко опишите запрос
									</Label>
									<Textarea
										id="message"
										value={formData.message}
										onChange={(e) => handleInputChange("message", e.target.value)}
										placeholder="Расскажите, с чем хотели бы поработать..."
										rows={4}
										className="text-sm sm:text-base resize-none bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-indigo-500"
									/>
								</div>

								<Button type="submit" disabled={isSubmitting} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg text-sm sm:text-base">
								{isSubmitting ? "Отправка..." : "Отправить заявку"}
								</Button>
								<p className="mt-2 text-xs text-slate-400 text-center">Нажимая "Отправить заявку", вы соглашаетесь на обработку персональных данных</p>
								</form>
								</CardContent>
								</Card>
				</div>
			</div>

			{/* Success Modal */}
			<Dialog open={showSuccess} onOpenChange={setShowSuccess}>
				<DialogContent className="sm:max-w-md mx-4 bg-white">
					<DialogHeader>
						<div className="flex items-center gap-3 mb-4">
							<div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center">
								<CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
							</div>
							<DialogTitle className="text-lg sm:text-xl text-slate-900">Заявка отправлена!</DialogTitle>
						</div>
					</DialogHeader>
					<p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">Спасибо за обращение! Я свяжусь с вами в течение 24 часов для согласования времени онлайн-сессии.</p>
					<p className="text-sm text-slate-500">Для более быстрой связи можете написать в WhatsApp или Telegram</p>
				</DialogContent>
			</Dialog>
		</section>
	)
}
